import React, { useRef, useState } from "react";
import {
  LoadingOutlined,
  UploadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";

import {
  Alert,
  Button,
  Card,
  Empty,
  Modal,
  notification,
  Space,
  Spin,
  Typography,
  Upload,
} from "antd";
import { api, baseURL } from "helpers/api";
import { useCookies } from "react-cookie";
import dayjs from "dayjs";

const IssueFiles = ({ issue_id, data }) => {
  const ref = useRef();
  const [{ access_token }] = useCookies(["access_token"]);
  const [delete_issue_image_id, setDeleteIssueImageId] = useState(null);
  const [is_deleting, setIsDeleting] = useState(false);

  const [fileList, setFileList] = useState([
    ...data.map(({ id: uid, file_url, file_name, user, created_at }) => ({
      uid,
      name: "file.pdf",
      status: "done",
      file_url,
      created_at,
      user_name: user?.full_name,
      file_name,
    })),
  ]);

  const handlePreview = (el) => {
    if (el.file_url) {
      window.open(el.file_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleChange = ({ file, fileList: newFileList }) => {
    if (file.status === "done" && file.response?.data) {
      const {
        id: uid,
        file_url,
        file_name,
        user,
        created_at,
      } = file.response.data;

      const updatedFileList = newFileList.map((item, index) =>
        index === newFileList.length - 1
          ? {
              ...item,
              uid,
              file_url,
              created_at,
              user_name: user?.full_name,
              file_name: file_name,
            }
          : item
      );

      setFileList(updatedFileList);
    } else if (file.status === "error") {
      const error_message =
        file?.response?.errors?.[0]?.message ||
        "nie udało się przesłać - spróbuj ponownie";

      notification.error({
        message: `${file.name} — ${error_message}`,
      });
      setFileList(newFileList.filter(({ status }) => status !== "error"));
    } else {
      setFileList(newFileList);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await api.delete(`/issue_files/${delete_issue_image_id}`);
      notification.success({
        message: "Plik został usunięty",
      });
      setFileList(fileList.filter(({ uid }) => uid !== delete_issue_image_id));
      setDeleteIssueImageId(null);
    } catch (error) {
      notification.error({
        message: "Nie udało się usunąć pliku",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const beforeUpload = (file) => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      notification.error({
        message: "Plik jest za duży",
        description: "Maksymalny rozmiar pliku to 5MB.",
      });
    }
    return isLt5M || Upload.LIST_IGNORE; // zablokuje dodanie pliku
  };

  return (
    <Card
      title={`Pliki (${fileList?.length || 0}/8)`}
      style={{ marginBottom: 24 }}
      extra={
        <Button
          disabled={fileList.length >= 8}
          type="primary"
          icon={<UploadOutlined />}
          onClick={() => ref?.current?.click()}
        >
          Dodaj
        </Button>
      }
    >
      <Alert
        className="m-b-10 ant-alert-default"
        message={
          <Space direction="vertical">
            <Typography.Text>
              Dostępne formaty: <strong>pdf</strong>
            </Typography.Text>
            <Typography.Text>
              Maksymalny rozmiar pliku: <strong>5 MB</strong>
            </Typography.Text>
          </Space>
        }
      />
      {delete_issue_image_id && (
        <Modal
          style={{ top: 45 }}
          title="Potwierdzenie"
          open={true}
          onOk={handleDelete}
          onCancel={() => setDeleteIssueImageId(null)}
          cancelText="Anuluj"
          okText="Potwierdzam"
          okType="danger"
          okButtonProps={{ loading: is_deleting }}
        >
          Czy na pewno chcesz usunąć plik?
        </Modal>
      )}
      {!fileList.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak danych" />
      )}
      <Upload
        accept="application/pdf"
        action={`${baseURL}/issue_files`}
        headers={{
          Authorization: `Bearer ${access_token}`,
        }}
        name="document"
        style={{ display: "none" }}
        data={{ issue_id }}
        listType="picture"
        beforeUpload={beforeUpload}
        fileList={fileList}
        itemRender={(_, file) => (
          <div
            className={`ant-upload-list-item ant-upload-list-item-${file.status}`}
          >
            <a
              onClick={(e) => {
                e.preventDefault();
                handlePreview(file);
              }}
              className="ant-upload-list-item-thumbnail"
              href={file?.file_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {file.status === "uploading" ? (
                <Spin
                  style={{ fontSize: 10 }}
                  indicator={<LoadingOutlined spin />}
                />
              ) : (
                <FilePdfOutlined />
              )}
            </a>
            <div className="bottom-info">
              <p>{file?.user_name || ""}</p>
              <p>{file?.file_name || ""}</p>
              <p>
                {file?.created_at
                  ? dayjs(file?.created_at).format("DD-MM-YYYY HH:mm")
                  : ""}
              </p>
            </div>
            <span className="ant-upload-list-item-actions picture">
              <button
                title="Usuń plik"
                type="button"
                className="ant-btn css-dev-only-do-not-override-15bnmwo ant-btn-text ant-btn-color-default ant-btn-variant-text ant-btn-sm ant-btn-icon-only ant-upload-list-item-action"
                onClick={async () => {
                  if (file.status === "error") {
                    setFileList(
                      fileList.filter((item) => item.uid !== file.uid)
                    );
                    return;
                  }
                  if (typeof file.uid === "string") {
                    setDeleteIssueImageId(file.uid);
                    return false;
                  }
                }}
              >
                <span className="ant-btn-icon">
                  <span
                    role="img"
                    aria-label="delete"
                    tabindex="-1"
                    className="anticon anticon-delete"
                  >
                    <svg
                      viewBox="64 64 896 896"
                      focusable="false"
                      data-icon="delete"
                      width="1em"
                      height="1em"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M360 184h-8c4.4 0 8-3.6 8-8v8h304v-8c0 4.4 3.6 8 8 8h-8v72h72v-80c0-35.3-28.7-64-64-64H352c-35.3 0-64 28.7-64 64v80h72v-72zm504 72H160c-17.7 0-32 14.3-32 32v32c0 4.4 3.6 8 8 8h60.4l24.7 523c1.6 34.1 29.8 61 63.9 61h454c34.2 0 62.3-26.8 63.9-61l24.7-523H888c4.4 0 8-3.6 8-8v-32c0-17.7-14.3-32-32-32zM731.3 840H292.7l-24.2-512h487l-24.2 512z"></path>
                    </svg>
                  </span>
                </span>
              </button>
            </span>
          </div>
        )}
        onChange={handleChange}
      >
        {fileList.length < 8 && (
          <Button ref={ref} type="primary" icon={<UploadOutlined />}>
            Dodaj
          </Button>
        )}
      </Upload>
    </Card>
  );
};
export default IssueFiles;
